import React, { useState, useEffect, useCallback } from 'react';
import { Activity, RefreshCw, CheckCircle, XCircle, MinusCircle, Loader } from 'lucide-react';
import api from '../utils/api';

const timeAgo = (iso) => {
  if (!iso) return 'Never';
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
};

const StatusIcon = ({ status }) => {
  if (status === 'success') return <CheckCircle className="h-4 w-4 text-green-400" />;
  if (status === 'failed') return <XCircle className="h-4 w-4 text-red-400" />;
  return <MinusCircle className="h-4 w-4 text-muted-foreground/50" />;
};

export const WebhookHealth = () => {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const res = await api.get('/api/webhooks/health');
      setData(res.data);
    } catch (e) {
      console.error('Webhook health load failed:', e);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div data-testid="webhook-health-panel" className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Activity className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold">Webhook Health</h3>
            <p className="text-sm text-muted-foreground">Last delivery status per event type</p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          data-testid="webhook-health-refresh-btn"
          className="p-2 hover:bg-muted rounded-lg transition-colors disabled:opacity-50"
          title="Refresh"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {!data ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          <Loader className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <div className="space-y-1" data-testid="webhook-health-list">
          {!data.webhook_configured && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-lg p-3 mb-3" data-testid="webhook-health-not-configured">
              N8N_WEBHOOK_URL is not configured — no webhooks are being delivered.
            </div>
          )}
          <div className="grid grid-cols-12 gap-2 px-3 py-1 text-xs text-muted-foreground font-medium">
            <span className="col-span-5">Event</span>
            <span className="col-span-3">Last Sent</span>
            <span className="col-span-2 text-center">Delivered</span>
            <span className="col-span-2 text-center">Failed</span>
          </div>
          {data.events.map((ev) => (
            <div
              key={ev.event}
              data-testid={`webhook-health-row-${ev.event}`}
              className="grid grid-cols-12 gap-2 items-center px-3 py-2 bg-background rounded-lg text-sm"
            >
              <span className="col-span-5 flex items-center gap-2 min-w-0">
                <StatusIcon status={ev.last_status} />
                <span className="truncate font-mono text-xs">{ev.event}</span>
              </span>
              <span className={`col-span-3 text-xs ${ev.last_sent ? 'text-foreground' : 'text-muted-foreground/50'}`}>
                {timeAgo(ev.last_sent)}
              </span>
              <span className="col-span-2 text-center text-xs text-green-400">{ev.success_count}</span>
              <span className={`col-span-2 text-center text-xs ${ev.failed_count > 0 ? 'text-red-400 font-bold' : 'text-muted-foreground/50'}`}>
                {ev.failed_count}
              </span>
              {ev.last_status === 'failed' && ev.last_error && (
                <p className="col-span-12 text-xs text-red-400/80 truncate pl-6" title={ev.last_error}>
                  {ev.last_error}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WebhookHealth;
